import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import axios from 'axios';
// 1. Import react-spring
import { animated, useSpring } from "react-spring";

/* ---------------------------------------------------------
   Design tokens (lifted from the original Tailwind config)
--------------------------------------------------------- */
const colors = {
  surfaceContainer: '#eceef0',
  primaryFixedDim: '#b7c4ff',
  secondaryFixed: '#dae2fd',
  surfaceContainerLowest: '#ffffff',
  inverseSurface: '#2d3133',
  secondaryFixedDim: '#bec6e0',
  tertiary: '#3f4f65',
  onPrimaryFixedVariant: '#0038b6',
  background: '#f7f9fb',
  onSecondaryContainer: '#5c647a',
  primaryContainer: '#0052ff',
  surfaceContainerLow: '#f2f4f6',
  inverseOnSurface: '#eff1f3',
  onSurfaceVariant: '#434656',
  errorContainer: '#ffdad6',
  surfaceVariant: '#e0e3e5',
  primaryFixed: '#dde1ff',
  secondary: '#565e74',
  outlineVariant: '#c3c5d9',
  onErrorContainer: '#93000a',
  onTertiaryFixedVariant: '#38485d',
  onSecondaryFixedVariant: '#3f465c',
  surface: '#f7f9fb',
  surfaceContainerHighest: '#e0e3e5',
  surfaceDim: '#d8dadc',
  surfaceBright: '#f7f9fb',
  onBackground: '#191c1e',
  onSecondary: '#ffffff',
  tertiaryFixed: '#d3e4fe',
  onSurface: '#191c1e',
  surfaceContainerHigh: '#e6e8ea',
  tertiaryContainer: '#57677e',
  primary: '#003ec7',
  error: '#ba1a1a',
  surfaceTint: '#004ced',
  tertiaryFixedDim: '#b7c8e1',
  onPrimaryContainer: '#dfe3ff',
  onPrimaryFixed: '#001452',
  onTertiaryFixed: '#0b1c30',
  inversePrimary: '#b7c4ff',
  onPrimary: '#ffffff',
  outline: '#737688',
  onTertiaryContainer: '#d6e6ff',
  onError: '#ffffff',
  onTertiary: '#ffffff',
  secondaryContainer: '#dae2fd',
  onSecondaryFixed: '#131b2e',
};

const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  marginMobile: '20px',
  gutterMobile: '12px',
};

/* ---------------------------------------------------------
   Global font imports (mirrors the <link> tags in the HTML)
--------------------------------------------------------- */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible+Next:wght@400;600;700&family=Lexend:wght@400;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  * {
    box-sizing: border-box;
  }
`;

/* ---------------------------------------------------------
   Layout primitives
--------------------------------------------------------- */
const Page = styled.div`
  font-family: 'Atkinson Hyperlegible Next', sans-serif;
  background: ${colors.background};
  color: ${colors.onBackground};
  min-height: max(884px, 100dvh);
  display: flex;
  flex-direction: column;
  -webkit-tap-highlight-color: transparent;
`;

const MaterialIcon = styled.span`
  font-family: 'Material Symbols Outlined';
  font-variation-settings: 'FILL' ${(p) => (p.$filled ? 1 : 0)}, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  font-size: ${(p) => p.$size || '24px'};
  line-height: 1;
  user-select: none;
`;

/* ---------------------------------------------------------
   Top App Bar
--------------------------------------------------------- */
const TopAppBar = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 50;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.outlineVariant}1a;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing.marginMobile};
  height: 56px;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.onSurfaceVariant};
  transition: background-color 0.2s, transform 0.2s;

  &:hover {
    background: ${colors.surfaceContainerLow};
  }
  &:active {
    transform: scale(0.95);
  }
`;

const AppBarTitle = styled.h1`
  font-family: 'Lexend', sans-serif;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${colors.onSurface};
  margin: 0;
`;

/* ---------------------------------------------------------
   Main content
--------------------------------------------------------- */
const Main = styled.main`
  flex-grow: 1;
  padding-top: 80px;
  padding-bottom: 128px;
  padding-left: ${spacing.marginMobile};
  padding-right: ${spacing.marginMobile};
  max-width: 672px;
  margin: 0 auto;
  width: 100%;
`;

const InstructionalCard = styled.div`
  margin-bottom: ${spacing.xl};
  padding: ${spacing.md};
  background: ${colors.surfaceContainerLow};
  border: 1px solid ${colors.outlineVariant}33;
  border-radius: 0.75rem;
`;

const InstructionalText = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.onSurfaceVariant};
  margin: 0 0 ${spacing.sm} 0;
`;

const TipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};
  align-items: center;
`;

const TipBadge = styled.span`
  background: ${colors.secondaryContainer};
  color: ${colors.onSecondaryFixed};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: 9999px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const TipText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.onSecondaryContainer};
  margin: 0;
`;

/* ---------------------------------------------------------
   Input section
--------------------------------------------------------- */
const InputSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const FieldGroup = styled.div``;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.onSurfaceVariant};
  margin-bottom: ${spacing.xs};
  margin-left: ${spacing.sm};
`;

const TextareaWrapper = styled.div`
  position: relative;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 192px;
  padding: ${spacing.md};
  background: ${colors.surfaceContainerLowest};
  border: 1px solid ${colors.outline};
  border-radius: 0.75rem;
  font-family: 'Lexend', sans-serif;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.01em;
  font-weight: 600;
  color: ${colors.primary};
  outline: none;
  resize: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: ${colors.outlineVariant}80;
  }

  &:focus {
    border-color: ${colors.primaryContainer};
    box-shadow: 0 0 0 4px ${colors.primaryContainer}33;
  }
`;

const CharCount = styled.span`
  position: absolute;
  right: ${spacing.md};
  bottom: ${spacing.md};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  font-weight: 700;
  color: ${(p) => (p.$active ? colors.primary : colors.outlineVariant)};
`;

/* ---------------------------------------------------------
   Chip preview
--------------------------------------------------------- */
const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  min-height: 44px;
  padding: ${spacing.sm};
  border: 2px dashed ${colors.outlineVariant}4d;
  border-radius: 0.75rem;
`;

const EmptyChipHint = styled.span`
  color: ${colors.outlineVariant};
  font-size: 14px;
  line-height: 20px;
  font-style: italic;
  padding: ${spacing.xs};
`;

const chipIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Chip = styled.div`
  background: ${colors.surfaceContainerHighest};
  color: ${colors.onSurface};
  padding: ${spacing.xs} ${spacing.md};
  border-radius: 9999px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  font-weight: 700;
  display: flex;
  align-items: center;
  animation: ${chipIn} 0.3s ease-out;
`;

const ChipRemove = styled(MaterialIcon)`
  font-size: 16px;
  margin-left: ${spacing.xs};
  cursor: pointer;
  color: inherit;

  &:hover {
    color: ${colors.error};
  }
`;

/* ---------------------------------------------------------
   Kinetic action button
--------------------------------------------------------- */
const ActionWrapper = styled.div`
  padding-top: ${spacing.xl};
`;

// Wraps our animated kinetic button with react-spring styled capabilities
const KineticButton = styled(animated.button)`
  background: ${(p) => p.$isSuccess ? '#2e7d32' : 'linear-gradient(135deg, #003ec7 0%, #0052ff 100%)'};
  width: 100%;
  height: 56px;
  border: none;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  color: ${colors.onPrimary};
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  box-shadow: ${(p) => p.$isSuccess ? '0 8px 24px rgba(46, 125, 50, 0.25)' : '0 8px 24px rgba(0, 62, 199, 0.25)'};
  cursor: pointer;
  transform: ${(p) => (p.$mounted ? 'translateY(0)' : 'translateY(16px)')};
  opacity: ${(p) => (p.$mounted ? 1 : 0)};
  transition: transform 0.5s, opacity 0.5s, background 0.3s, box-shadow 0.3s;

  &:hover {
    opacity: ${(p) => (p.$mounted ? 0.9 : 0)};
  }
  &:active {
    transform: ${(p) => (p.$mounted ? 'scale(0.95)' : 'translateY(16px)')};
  }
`;

/* ---------------------------------------------------------
   Atmospheric image strip
--------------------------------------------------------- */
const AtmosphereWrapper = styled.div`
  margin-top: ${spacing.xl};
  opacity: 0.2;
  pointer-events: none;
  user-select: none;
  overflow: hidden;
  height: 128px;
  position: relative;
`;

const AtmosphereImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  border-radius: 0.75rem;
  filter: grayscale(1);
  background-image: url('https://lh3.googleusercontent.com/aida-public/AB6AXuB_G1gORtu1Yz0doyon8tGu9iLPyqaLHkJUgVVIjJKU2E5D1wHD52Kxrp6n3ZJAbhiH7O6BtpG5Ecu_8UMe8af_gBriheVgWfOO_555GR6tOLfTjbISjHkXZJcYs_hA26D6ntjXbOLI-T0dkotJ0D87vQkLK3LoUPTvSMQ5CS8_a3kD_jfeQlCPprL7byYhRper6F9fKfy6FsLY887eDEOozFOM7Atn3ci_YaxC3UNy3BxwDFdEn3Ld8pKenOnNirg0D_VKxrZlVg');
`;

/* ---------------------------------------------------------
   Bottom nav
--------------------------------------------------------- */
const BottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 50;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: ${spacing.sm} ${spacing.lg};
  padding-bottom: max(${spacing.sm}, env(safe-area-inset-bottom));
  background: ${colors.surfaceContainer};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
`;

const NavItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s, opacity 0.15s;
  color: ${(p) => (p.$active ? colors.onPrimaryContainer : colors.onSurfaceVariant)};
  background: ${(p) => (p.$active ? colors.primaryContainer : 'transparent')};
  border-radius: 9999px;
  padding: ${(p) => (p.$active ? '4px 16px' : '0')};

  &:hover {
    opacity: 0.8;
  }
  &:active {
    transform: scale(0.9);
  }
`;

const NavLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-top: 2px;
`;

/* ---------------------------------------------------------
   Component
--------------------------------------------------------- */
export default function Admin() {
  const [value, setValue] = useState('');
  const [mounted, setMounted] = useState(false);
  // Track success status for the feedback animation
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const teams = value
    .split(/[\n,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const removeTeam = (idx) => {
    const next = teams.filter((_, i) => i !== idx);
    setValue(next.join(', '));
  };

  // Define spring animation styles for the button's visual feedback on success
  const buttonSpring = useSpring({
    transform: isSuccess ? 'scale(1.05)' : 'scale(1)',
    config: { tension: 300, friction: 15 },
  });

  const runTeams = () => {
    axios({
      method: 'post',
      url: `https://dhqs56xckk.execute-api.us-east-2.amazonaws.com/pred_dev/tpull`,
      withCredentials: false,
      data: {
        teams: teams
      },
    })
    .then((res) => {
      // Trigger the success spring animation sequence
      setIsSuccess(true);
      
      // Revert the state back to normal after 2 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
    })
    .catch((err) => {
      console.error(err);
      alert(err)
    });
  }

  return (
    <Page>
      <GlobalStyle />

      <TopAppBar>
        <IconButton aria-label="Close">
          <MaterialIcon>close</MaterialIcon>
        </IconButton>
        <AppBarTitle>Submit Teams</AppBarTitle>
        <IconButton aria-label="Help">
          <MaterialIcon>help_outline</MaterialIcon>
        </IconButton>
      </TopAppBar>

      <Main>
        <InstructionalCard>
          <InstructionalText>
            Enter the short codes for the teams you wish to track. Separate each team with a comma or new line.
          </InstructionalText>
          <TipRow>
            <TipBadge>PRO TIP</TipBadge>
            <TipText>Example: 'nyc', 'gsw', 'lal'</TipText>
          </TipRow>
        </InstructionalCard>

        <InputSection>
          <FieldGroup>
            <FieldLabel htmlFor="team-input">Team Shortcodes</FieldLabel>
            <TextareaWrapper>
              <Textarea
                id="team-input"
                placeholder="nyc, gsw, chi..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <CharCount $active={teams.length > 0}>
                {teams.length} {teams.length === 1 ? 'Team' : 'Teams'} Detected
              </CharCount>
            </TextareaWrapper>
          </FieldGroup>

          <ChipContainer>
            {teams.length > 0 ? (
              teams.map((team, idx) => (
                <Chip key={`${team}-${idx}`}>
                  {team.toUpperCase()}
                  <ChipRemove onClick={() => removeTeam(idx)}>cancel</ChipRemove>
                </Chip>
              ))
            ) : (
              <EmptyChipHint>Live preview of your teams will appear here...</EmptyChipHint>
            )}
          </ChipContainer>

          <ActionWrapper>
            <KineticButton 
              $mounted={mounted} 
              $isSuccess={isSuccess}
              style={buttonSpring}
              onClick={runTeams}
            >
              {isSuccess ? (
                <>
                  <MaterialIcon $filled $size="20px">check_circle</MaterialIcon>
                  <span>Sent Successfully!</span>
                </>
              ) : (
                <>
                  <MaterialIcon $filled $size="20px">send</MaterialIcon>
                  <span>Send Submission</span>
                </>
              )}
            </KineticButton>
          </ActionWrapper>
        </InputSection>

        <AtmosphereWrapper>
          <AtmosphereImage
            role="img"
            aria-label="A sophisticated abstract architectural background with high-performance minimalist aesthetics."
          />
        </AtmosphereWrapper>
      </Main>

      <BottomNav>
        <NavItem $active href="#">
          <MaterialIcon $filled>groups</MaterialIcon>
          <NavLabel>Teams</NavLabel>
        </NavItem>
        <NavItem href="#">
          <MaterialIcon>history</MaterialIcon>
          <NavLabel>History</NavLabel>
        </NavItem>
      </BottomNav>
    </Page>
  );
}